import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
import type {Key, Node, Selection} from '@react-types/shared';
import {
  AnimatePresenceProvider,
  assignProps,
  createView,
  createViewConfig,
  view,
} from '@sail/engine';
import {arrowUpDown} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import type {
  Expandable,
  Filterable,
  ListCollectionComponent,
  MenuTriggerProps,
  MultiSelectState,
  SingleSelectionProps,
  MultipleSelectionProps,
  TreeState,
  ListCollection,
} from '@sail/react-aria';
import {
  FocusScope,
  mapSelectionToKeysSet,
  useListCollection,
  useMultiSelectState,
  useSelect,
} from '@sail/react-aria';
import {FormField, useFormFieldProps} from './FormField';
import {Icon} from './Icon';
import {Button} from './Button';
import {Popover} from './popover';
import {ListBox, ListBoxContext, ListBoxConfig} from './ListBox';
import {useFieldContexts} from '../hooks/useFieldContexts';
import type {FormFieldProps} from './FormField';
import type {ButtonProps} from './Button';
import {SearchField, SearchFieldContext} from './SearchField';
import {
  DO_NOT_USE_FauxTree as FauxTree,
  FauxTreeContext,
  FauxTreeConfig,
} from './FauxTree';
import type {FauxTreeGroupProps} from './FauxTreeGroup';
import {FauxTreeGroupTransforms, FauxTreeGroup} from './FauxTreeGroup';
import type {FauxTreeItemProps} from './FauxTreeItem';
import {
  FauxTreeItemTransforms,
  FauxTreeItem,
  FauxTreeItemConfig,
} from './FauxTreeItem';
import {ListBoxGroup} from './ListBoxGroup';
import {ListBoxItem, ListBoxItemConfig} from './ListBoxItem';
import {SelectFieldConfig} from './SelectFieldConfig';
import {Tooltip} from './Tooltip';
import {
  normalizeSelectionProps,
  validateSelectionProps,
} from './utils/selectionHelpers';
import {getVisibleItemsAndOverflow} from '../lib/getVisibleItemsAndOverflow';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {useSelectFieldFormAdapter} from './form/adapters/useSelectFieldFormAdapter';
import {useFormContext} from './form/useFormContext';
import {css} from '../css';

type MessagesConfig = {
  selectedText?: (t: number) => string;
  numberOfItemsText?: (t: number) => string;
  andMoreText?: (t: number) => string;
};

const defaultAndMoreItemsText = (itemsCount: number) =>
  `and ${itemsCount} more`;

const defaultNumberOfItemsText = (itemsCount: number) => `${itemsCount} items`;

const useAutofocus = (state: MultiSelectState & TreeState) => {
  const autoFocusRef = useRef(true);

  useEffect(() => {
    // Reset the autofocus flag when the list closes or opens.
    autoFocusRef.current = true;
  }, [state.isOpen]);

  useEffect(() => {
    // If the list is open and there are items, focus the first selected item.
    // If there's no selected item, leave focus on the list itself instead of
    // focusing the first item to mantain compatibility with the previous behavior.
    if (autoFocusRef.current && state.isOpen && state.collection.size > 0) {
      const manager = state.selectionManager;
      const firstSelectedItem = Array.from(manager.selectedKeys).find((key) =>
        manager.canSelectItem(key),
      );

      if (firstSelectedItem) {
        manager.setFocused(true);
        manager.setFocusedKey(firstSelectedItem);
      }

      autoFocusRef.current = false;
    }
  }, [state]);
};

const getCollectionItemsLength = (
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
) => {
  return collection.getItems().reduce((acc, item) => {
    if (item.childItems) {
      return acc + item.childItems.length;
    } else {
      return acc + 1;
    }
  }, 0);
};

const useVisibleSelectedItems = (
  selectedItems: (Node<unknown> | null)[] | undefined,
  itemsTotalCount: number,
  itemsContainerRef: React.RefObject<HTMLDivElement>,
  onItemsVisibleChange: (visible: boolean) => void,
) => {
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(0);
  const [firstItemOverflows, setFirstItemOverflows] = useState<boolean>(false);
  const itemsRef = useRef<React.MutableRefObject<HTMLSpanElement>[]>(
    Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    ),
  );

  useLayoutEffect(() => {
    itemsRef.current = Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    );
  }, [itemsTotalCount]);

  useLayoutEffect(() => {
    if (!selectedItems || !itemsContainerRef.current || !itemsRef.current) {
      return;
    }

    const {visibleItemsCount, firstItemOverflows} = getVisibleItemsAndOverflow(
      selectedItems,
      itemsContainerRef,
      itemsRef,
    );

    setVisibleItemsCount(visibleItemsCount);
    setFirstItemOverflows(firstItemOverflows);

    onItemsVisibleChange(
      !firstItemOverflows && visibleItemsCount === selectedItems.length,
    );
  }, [selectedItems, itemsContainerRef, onItemsVisibleChange]);

  return {visibleItemsCount, firstItemOverflows};
};

const MultipleSelectionButtonContent: React.FC<{
  selectedItems: (Node<unknown> | null)[] | undefined;
  itemsTotalCount: number;
  messages?: MessagesConfig;
  onItemsVisibleChange: (visible: boolean) => void;
}> = ({itemsTotalCount, selectedItems, messages, onItemsVisibleChange}) => {
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  const {visibleItemsCount, firstItemOverflows} = useVisibleSelectedItems(
    selectedItems,
    itemsTotalCount,
    itemsContainerRef,
    onItemsVisibleChange,
  );

  if (!selectedItems || selectedItems.length === 0) {
    return null;
  }

  return (
    <view.div
      ref={itemsContainerRef}
      css={{
        flexGrow: 1,
        stack: 'x',
        gap: 'xsmall',
        overflowX: 'hidden',
      }}
    >
      <view.div
        css={
          firstItemOverflows
            ? css({
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              })
            : css({flexGrow: 0})
        }
      >
        {Array.from({length: visibleItemsCount}).map((_, index) => {
          const selectedItem = selectedItems[index];
          if (!selectedItem) {
            return null;
          }
          return (
            <React.Fragment key={selectedItem.key}>
              <view.span>
                {selectedItem.props?.children || selectedItem.textValue}
                {index + 1 < visibleItemsCount ? ', ' : ''}
              </view.span>
            </React.Fragment>
          );
        })}
      </view.div>
      {visibleItemsCount < selectedItems.length && visibleItemsCount > 0 ? (
        <view.span
          css={{
            flexGrow: 0,
          }}
        >
          {messages?.andMoreText
            ? messages.andMoreText(selectedItems.length - visibleItemsCount)
            : defaultAndMoreItemsText(selectedItems.length - visibleItemsCount)}
        </view.span>
      ) : null}
      {visibleItemsCount === 0 ? (
        <view.span
          css={{
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {messages?.numberOfItemsText
            ? messages.numberOfItemsText(selectedItems.length)
            : defaultNumberOfItemsText(selectedItems.length)}
        </view.span>
      ) : null}
    </view.div>
  );
};

const TruncatedText = createView('span', {
  css: {
    overflowX: 'hidden',
    overflowY: 'visible',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

interface ListContextValue {
  selectionMode?: SelectionProps['selectionMode'];
}

const ListContext = createContext<ListContextValue>({});

export const useListContext = () => {
  return useContext(ListContext);
};

// Item

export type SelectFieldItemProps = FauxTreeItemProps;
export const SelectFieldItemTransforms = FauxTreeItemTransforms;

export const SelectFieldItemConfig = createViewConfig({
  name: 'SelectItem',
  props: {} as SelectFieldItemProps,
  flattens: true,
});

export const SelectFieldItem = SelectFieldItemConfig.createView(
  (props: FauxTreeItemProps) => {
    const {selectionMode} = useListContext();
    return selectionMode === 'multiple' ? (
      <FauxTreeItem {...props} />
    ) : (
      <ListBoxItem {...props} />
    );
  },
);

// Group

export type SelectFieldGroupProps = FauxTreeGroupProps;
export const SelectFieldGroupTransforms = FauxTreeGroupTransforms;

export const SelectFieldGroupConfig = createViewConfig({
  name: 'SelectGroup',
  props: {} as SelectFieldGroupProps,
  flattens: true,
});

export const SelectFieldGroup = SelectFieldGroupConfig.createView(
  (props: FauxTreeGroupProps) => {
    const {selectionMode} = useListContext();
    return selectionMode === 'multiple' ? (
      <FauxTreeGroup {...props} />
    ) : (
      <ListBoxGroup {...props} />
    );
  },
);

// SelectField

export type SelectionProps<CanClearSelection extends boolean = false> = Omit<
  SingleSelectionProps & MultipleSelectionProps,
  'disallowEmptySelection' | 'onSelectionChange'
> & {
  /** Handler that is called when the selection changes for multiple-selection fields. */
  onMultiSelectionChange?: (keys: Selection) => void;

  /** Handler that is called when the selection changes for single-selection fields. */
  onSelectionChange?: (
    key: [CanClearSelection] extends [true] ? Key | null : Key,
  ) => unknown;
};

export type SelectFieldProps<
  T,
  CanClearSelection extends boolean = false,
> = FormFieldProps<
  ListCollectionComponent<
    T,
    SelectFieldItemProps,
    typeof SelectFieldItemTransforms.defaultField,
    SelectFieldGroupProps,
    typeof SelectFieldGroupTransforms.idField
  > &
    Expandable &
    Filterable<T> &
    SelectionProps<CanClearSelection> &
    MenuTriggerProps & {
      /** The placeholder text to display when no value is selected */
      placeholder?: string;
      /** Whether the input is disabled. */
      disabled?: boolean;
      disabledKeys?: Iterable<Key>;
      /** Whether the input can be selected but not changed by the user. */
      readOnly?: boolean;
      subviews?: View.Subviews<{
        button: typeof Button;
        buttonIcon: typeof Icon;
        popover: typeof Popover;
        value: 'span';
      }>;
      size?: ButtonProps['size'];
      ['data-testid']?: string;
      messages?: MessagesConfig;
      /**  Set to false to hide the option to select/unselect all items */
      selectionAllEnabled?: boolean;
      /**
       * Enable the clear selection option.
       * Only for `selectionMode=single`. Multi-selection fields already support clearing the selection by default.
       *
       * Requires that the `placeholder` prop is defined.
       *
       * While we plan to support these in the future, for now `filterable` and `group` must not be defined.
       * */
      clearable?: CanClearSelection;
      /**
       * Set to false to hide the option to select/unselect all items in a group.
       *
       * Only applies when `group` prop is set and `selectionMode=multiple`.
       */
      groupSelectionAllEnabled?: boolean;
    },
  typeof Button
>;

export const SelectField = SelectFieldConfig.createGenericView(
  <T extends object, CanClearSelection extends boolean = false>({
    data,
    item,
    group,
    children,
    placeholder,
    filterable,
    defaultFilter,
    messages,
    size,
    selectionAllEnabled,
    clearable,
    groupSelectionAllEnabled,
    ...restProps
  }: View.RenderProps<SelectFieldProps<T, CanClearSelection>>) => {
    validateSelectionProps(restProps);

    const isInSailForm = !!useFormContext()?.control;
    const isMultiSelect = restProps.selectionMode === 'multiple';

    const {valueAdapter, changeAdapter} =
      useSelectFieldFormAdapter(isMultiSelect);

    const [fieldProps, props] = useFormFieldProps(restProps, {
      valueProp: isMultiSelect ? 'selectedKeys' : 'selectedKey',
      changeProp: isMultiSelect
        ? 'onMultiSelectionChange'
        : 'onSelectionChange',
      valueAdapter,
      changeAdapter,
    });

    const {label} = fieldProps;

    const collection = useListCollection({
      children,
      data,
      item,
      group,
      itemTransforms: SelectFieldItemTransforms,
      groupTransforms: SelectFieldGroupTransforms,
    });

    if (isInSailForm && !isMultiSelect) {
      const singleChangeHandler = props.onSelectionChange;
      props.onSelectionChange = (key) => {
        singleChangeHandler?.(key);
        // For single selection, call onBlur because dropdown closes
        if ('onBlur' in props) {
          props.onBlur?.();
        }
      };
    }

    const state = useMultiSelectState({
      getItems: collection.getItems,
      selection: {
        ...props,
        ...normalizeSelectionProps(props, collection, true),
      },
      trigger: props,
      expandable: props,
    });

    const buttonRef = useRef<HTMLAnchorElement>(null);

    const {labelProps, triggerProps, valueProps, listboxProps} = useSelect(
      {isDisabled: props.disabled || props.readOnly || false},
      state,
      buttonRef,
    );

    const [triggerTooltipEnabled, setTriggerTooltipEnabled] =
      useState<boolean>(false);

    if (children && filterable) {
      throw new Error(
        'SelectField with the `filterable` prop was provided items as JSX children. This is not supported; ' +
          'to use filtering, provide items with the `data` prop',
      );
    }

    // Autofocus the first item when the list opens. This is a workaround to make autofocus
    // work with our collections API. Our collections are built differently from react-aria's
    // collections. We only add items to the collection when they're rendered while react-aria
    // expects all items to be in the collection before the list is opened. This means that when
    // the list is opened, react-aria has already triggered focus on an item that doesn't exist
    // in the collection and we need to manually trigger focus.
    useAutofocus(state);

    const {isTouch, isSmallScreen} = useMediaQuery();
    const isMobileAndTouch = isSmallScreen && isTouch;

    const searchFieldProps = {
      label,
      data,
      defaultFilter,
      item,
      group,
      expandable: props.expandable,
      defaultExpandedKeys: props.defaultExpandedKeys,
      expandedKeys: props.expandedKeys,
      disabledKeys: props.disabledKeys,
      defaultValue: '',
      shouldCloseOnBlur: false,
      isOpen: true, // If we are rendering the searchfield, we always want to render the FauxTree below it
      css: [
        css({
          borderRadius: 0,
          backgroundColor: 'form',
          // match Popover's border radius
          borderTopLeftRadius: 'medium',
          borderTopRightRadius: 'medium',
        }),
        isMobileAndTouch
          ? css({borderBottom: 'none'})
          : css({borderBottom: 'solid 1px', borderBottomColor: 'border'}),
      ],
      subviews: {
        inputs: {
          css: [
            css({
              borderTopLeftRadius: 'xsmall',
              borderTopRightRadius: 'xsmall',
              // margin: xsmall is used to provision enough space for the focus ring
              // in order to visually appear "inset"
              margin: 'xsmall',
            }),
            isMobileAndTouch
              ? css({
                  borderBottomLeftRadius: 'xsmall',
                  borderBottomRightRadius: 'xsmall',
                  marginX: 'space.250',
                  keyline: 'border',
                  marginTop: 'space.150',
                  marginBottom: 'xsmall',
                  borderBottom: 'none',
                })
              : css({
                  keyline: 'transparent',
                  ':focus-within': {
                    keyline: 'transparent',
                  },
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }),
          ],
        },
      },
    };

    const itemVisibleChangeHandler = useCallback(
      (allItemsVisible: boolean) => setTriggerTooltipEnabled(!allItemsVisible),
      [setTriggerTooltipEnabled],
    );

    // Check if the children contain a SelectFieldGroup. Equivalent for `data` would be the `group` prop.
    const hasChildrenSelectFieldGroup =
      Array.isArray(children) &&
      children.some(
        (child) => child?.type?.config?.options?.name === 'SelectGroup',
      );

    const shouldShowClearSelection =
      !!clearable &&
      !!placeholder &&
      !isMultiSelect &&
      !filterable &&
      !group &&
      !hasChildrenSelectFieldGroup;

    if (clearable && !shouldShowClearSelection) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          'SelectField: "clearable" is enabled, but the clear selection item will not be shown. You are probably missing a `placeholder` prop, or are using an unsupported combination of props like `selectionMode=multiple`, `filterable` or `group`.',
        );
      }
    }

    const listbox = (
      <AnimatePresenceProvider>
        <FocusScope restoreFocus autoFocus={filterable}>
          <ListBoxConfig.Customize
            css={[css({keyline: 'transparent', backgroundColor: 'form'})]}
          >
            <ListBoxItemConfig.Customize
              css={[
                isMobileAndTouch
                  ? css({paddingX: 'small', paddingY: 'space.75'})
                  : css({}),
              ]}
            >
              {filterable ? (
                <SearchFieldContext.Provider
                  value={{collection, options: {shouldUsePopover: false}}}
                >
                  <SearchField
                    {...searchFieldProps}
                    menuTrigger="focus"
                    hiddenElements={['label']}
                    size={isMobileAndTouch ? 'large' : undefined}
                    selectedKey={
                      [...mapSelectionToKeysSet(state.selectedKeys)]?.[0] ??
                      null
                    }
                    subviews={{
                      ...searchFieldProps.subviews,
                      listbox: {
                        css: [
                          isMobileAndTouch
                            ? css({
                                flexGrow: '0',
                                alignY: 'top',
                                height: 'fill',
                                paddingX: 'space.150',
                                paddingY: 'space.0',
                                marginY: 'small',
                              })
                            : css({}),
                          css({
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }),
                        ],
                      },
                    }}
                    onSelectionChange={(key) => {
                      if (key) {
                        state.setSelectedKeys(new Set([key]));
                        state.close();
                      }
                    }}
                    onSubmit={(_value, results) => {
                      if (!state.isFocused) {
                        const key = Array.from(results).find(
                          (key) =>
                            state.collection.getItem(key)?.type !== 'section',
                        );

                        if (key) {
                          state.setSelectedKeys(new Set([key]));
                          state.close();
                        }
                      }
                    }}
                  />
                </SearchFieldContext.Provider>
              ) : (
                <ListBoxContext.Provider
                  value={{state, collection, options: listboxProps}}
                >
                  <ListBox
                    label={label}
                    clearable={
                      shouldShowClearSelection
                        ? {label: placeholder}
                        : undefined
                    }
                  />
                </ListBoxContext.Provider>
              )}
            </ListBoxItemConfig.Customize>
          </ListBoxConfig.Customize>
        </FocusScope>
      </AnimatePresenceProvider>
    );

    const fauxTree = (
      <AnimatePresenceProvider>
        <FocusScope restoreFocus autoFocus={filterable}>
          <FauxTreeConfig.Customize
            css={[
              css({keyline: 'transparent', backgroundColor: 'form'}),
              isMobileAndTouch
                ? css({paddingY: 'space.150', flewGrow: '0'})
                : css({}),
            ]}
          >
            <FauxTreeItemConfig.Customize
              css={
                isMobileAndTouch
                  ? css({paddingX: 'small', paddingY: 'space.75'})
                  : css({})
              }
            >
              {filterable ? (
                <SearchFieldContext.Provider
                  value={{collection, options: {shouldUsePopover: false}}}
                >
                  <SearchField
                    {...searchFieldProps}
                    menuTrigger="focus"
                    hiddenElements={['label']}
                    size={isMobileAndTouch ? 'large' : undefined}
                    selectedKeys={state.selectedKeys}
                    selectionMode="multiple"
                    messages={messages}
                    selectionAllEnabled={selectionAllEnabled}
                    groupSelectionAllEnabled={groupSelectionAllEnabled}
                    subviews={{
                      ...searchFieldProps.subviews,
                      listbox: {
                        css: [
                          isMobileAndTouch
                            ? css({
                                alignY: 'top',
                                height: 'fill',
                                paddingY: 'none',
                                marginY: 'small',
                              })
                            : css({}),
                          css({
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }),
                        ],
                      },
                    }}
                    onMultiSelectionChange={(keys: Selection) => {
                      state.setSelectedKeys(keys);
                    }}
                    onSubmit={(_value, results) => {
                      if (!state.isFocused) {
                        const key = Array.from(results).find(
                          (key) =>
                            state.collection.getItem(key)?.type !== 'section',
                        );

                        if (key) {
                          const mappedSelectedKeys = mapSelectionToKeysSet(
                            state.selectedKeys,
                          );

                          const isSelected = mappedSelectedKeys.has(key);
                          if (!isSelected) {
                            state.setSelectedKeys([...mappedSelectedKeys, key]);
                          }
                        }
                      }
                    }}
                  />
                </SearchFieldContext.Provider>
              ) : (
                <FauxTreeContext.Provider
                  value={{state, collection, options: listboxProps}}
                >
                  <FauxTree
                    label={label}
                    data={data}
                    item={item}
                    group={group}
                    messages={messages}
                    selectionAllEnabled={selectionAllEnabled}
                    groupSelectionAllEnabled={groupSelectionAllEnabled}
                  >
                    {children}
                  </FauxTree>
                </FauxTreeContext.Provider>
              )}
            </FauxTreeItemConfig.Customize>
          </FauxTreeConfig.Customize>
        </FocusScope>
      </AnimatePresenceProvider>
    );

    const {selectedItems} = state;

    const itemsLength = useMemo(() => {
      return getCollectionItemsLength(collection);
    }, [collection]);

    return (
      <FormField
        {...fieldProps}
        subviews={{
          label: {
            uses: [assignProps(labelProps as View.IntrinsicElement<'label'>)],
          },
          inputs: {css: {minWidth: '0'}},
        }}
        css={{minWidth: 240}}
      >
        <Button
          ref={buttonRef}
          {...valueProps}
          data-testid={props['data-testid']}
          css={{
            stack: 'x',
            display: 'block',
            baselineAlign: 'auto',
            gap: 'space.75',
            // buttons have `width: 'fit'` by default, but fields stretch to fill the available space
            width: 'auto',
            alignY: 'center',
            // Appearance
            topShadow: 'form',
            keyline: 'form',
            backgroundColor: 'form',
            textColor: 'form',
            border: 'none',
            borderRadius: 'form',

            // Hovered
            ':hover:not(:active):not([aria-disabled="true"])': {
              topShadow: 'form.hovered',
              keyline: 'form.hovered',
              backgroundColor: 'form.hovered',
              textColor: 'form.hovered',
            },
            // Focus
            ':focus': {
              focusRing: 'focus',
              topShadow: 'form.focused',
              keyline: 'form.focused',
              backgroundColor: 'form.focused',
              textColor: 'form.focused',
            },

            ':active:not([aria-disabled="true"])': {
              topShadow: 'form.pressed',
              keyline: 'form.pressed',
              backgroundColor: 'form.pressed',
              textColor: 'form.pressed',
            },

            // Error styles
            '[aria-invalid="true"]': {
              keyline: 'form.error',
            },

            // Disabled styles
            '[aria-disabled="true"]': {
              color: 'form.disabled',
              backgroundColor: 'form.disabled',
              borderColor: 'form.disabled',
              pointerEvents: 'none',
              topShadow: '0 0 0 0 transparent',
            },
            // turn off "wash" effect over disabled buttons
            '[aria-disabled="true"]::after': {
              display: 'none',
            },
          }}
          size={size}
          type="secondary"
          inherits={restProps.subviews.button}
          uses={[
            assignProps((props) => {
              const contextProps = useFieldContexts(props);
              return contextProps;
            }),
            assignProps(triggerProps as ButtonProps),
            assignProps((props) => ({
              // truncation styles can't be applied to flex containers.
              // and wrapping the entire content would break JSX-based item layouts
              children: React.Children.map(props.children, (child) =>
                typeof child === 'string' ? (
                  <TruncatedText> {child}</TruncatedText>
                ) : (
                  child
                ),
              ),
            })),
          ]}
        >
          {!selectedItems || selectedItems.length === 0 ? placeholder : null}
          {props.selectionMode === 'single'
            ? selectedItems?.[0]?.props.icon
            : null}
          {props.selectionMode === 'single'
            ? selectedItems?.[0]?.props.children ||
              selectedItems?.[0]?.textValue
            : null}
          {props.selectionMode === 'multiple' ? (
            <MultipleSelectionButtonContent
              itemsTotalCount={itemsLength}
              selectedItems={selectedItems}
              onItemsVisibleChange={itemVisibleChangeHandler}
              messages={messages}
            />
          ) : null}
          <Icon
            inherits={restProps.subviews.buttonIcon}
            icon={arrowUpDown}
            aria-hidden
            size="xsmall"
            css={{marginLeft: 'auto'}}
          />
        </Button>
        {!state.isOpen &&
        props.selectionMode === 'multiple' &&
        triggerTooltipEnabled &&
        selectedItems &&
        selectedItems.length > 0 ? (
          <Tooltip
            trigger={buttonRef}
            placement="top"
            delay={{show: 0, hide: 0}}
          >
            <view.div
              css={{
                display: '-webkit-box',
                lineClamp: 10,
                WebkitLineClamp: 10,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedItems
                .map((item) => {
                  return item?.props.children || item?.textValue;
                })
                .join(', ')}
            </view.div>
          </Tooltip>
        ) : null}
        {/*
          This is a tradeoff we made when allowing items as renderable
          children. Unfortunately, we *always* need to render the collection
          so the items are always registered. For anything but simple cases,
          we will encourage users to use the `data` pattern but it's still
          worthwhile to support this for simple cases.
         */}
        <ListContext.Provider value={{selectionMode: props.selectionMode}}>
          <AnimatePresenceProvider
            emptyRender={
              children ? (
                <view.div css={{display: 'none'}}>
                  {props.selectionMode === 'multiple' ? fauxTree : listbox}
                </view.div>
              ) : null
            }
          >
            <Popover
              inherits={restProps.subviews.popover}
              open={state.isOpen}
              targetRef={buttonRef}
              placement="bottom left"
              shift={false}
              size={{
                maxHeight: 'fill',
                matchWidth: true,
              }}
              onClose={() => {
                state.close();
                if (isInSailForm && 'onBlur' in props) {
                  props.onBlur?.();
                }
              }}
              subviews={{
                mobile: {
                  css: filterable
                    ? css({
                        // We want Popover in SelectField to take as much space of
                        // the viewport as possible if there's a keyboard
                        // (filterable), so the input to filter results is never
                        // hidden by the keyboard.
                        height: 'fill',
                      })
                    : css({
                        height: 'auto',
                      }),
                },
              }}
              css={{stack: 'y'}}
              mobileTitle={label || placeholder || props['aria-label']}
            >
              {props.selectionMode === 'multiple' ? fauxTree : listbox}
            </Popover>
          </AnimatePresenceProvider>
        </ListContext.Provider>
      </FormField>
    );
  },
);
